<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="opportunities"/>
        <p class="pi-page__lead">
          Funcionalidades de venda avulsa ainda não liberadas para empresas pagantes, com o sinal que levou a cada card.
          Só fatos: sem valor estimado e sem probabilidade. O estado comercial vem das liberações e vendas registradas.
        </p>

        <section class="pi-section" aria-labelledby="pi-opp-rules">
          <div class="pi-section__head">
            <h2 id="pi-opp-rules" class="pi-section__title">
              Regras<pi-definition-tooltip class="pi-opp-info" info-key="revenue.rule" label="Regras"/>
            </h2>
            <p class="pi-section__hint">Regra ligada sem dados suficientes fica aguardando e não gera oportunidade.</p>
          </div>
          <p class="pi-opp-legend">
            <span class="pi-opp-legend__lead">Selos das regras:</span>
            <span v-for="item in ruleLegend" :key="item.infoKey" class="pi-opp-legend__item">
              {{ item.label }}<pi-definition-tooltip class="pi-opp-info" :info-key="item.infoKey" :label="item.label"/>
            </span>
          </p>
          <pi-data-state :loading="rules.loading" :error="rulesFailure" :envelope="rules.envelope" :empty="rulesEmpty"
                         empty-message="Nenhuma regra cadastrada." :show-footer="false" @retry="loadRules(true)">
            <ul class="pi-opp-rules">
              <li v-for="rule in ruleRows" :key="rule.key" class="pi-opp-rule">
                <div class="pi-opp-rule__head">
                  <strong class="pi-opp-rule__name">{{ rule.name }}</strong>
                  <span class="pi-badge" :class="rule.enabled ? 'pi-badge--ok' : 'pi-badge--muted'">{{ rule.enabledText }}</span>
                  <span v-if="rule.maturityText" class="pi-badge pi-badge--info">{{ rule.maturityText }}</span>
                  <span v-if="rule.partialText" class="pi-badge pi-badge--warn">{{ rule.partialText }}</span>
                </div>
                <p v-if="rule.description" class="pi-opp-rule__text">{{ rule.description }}</p>
                <p v-if="rule.reasonText" class="pi-opp-rule__text">{{ rule.reasonText }}</p>
                <p class="pi-opp-rule__meta">{{ rule.metaText }}</p>
              </li>
            </ul>
          </pi-data-state>
        </section>

        <section class="pi-section" aria-labelledby="pi-opp-list">
          <div class="pi-section__head">
            <h2 id="pi-opp-list" class="pi-section__title">Oportunidades</h2>
            <span class="pi-muted" role="status">{{ countText }}</span>
            <pi-export-menu title="Oportunidades" screen="Oportunidades" :columns="exportColumns" :source="exportSource" :filters="exportFilters"
                            :disabled="!list.loaded || !!list.error"/>
          </div>
          <p class="pi-section__hint pi-opp-order">{{ orderText }}</p>
          <p class="pi-opp-legend">
            <span class="pi-opp-legend__lead">Como ler cada cartão:</span>
            <span v-for="column in cardLegend" :key="column.key" class="pi-opp-legend__item">
              {{ column.label }}<pi-definition-tooltip class="pi-opp-info" :info-key="column.infoKey" :label="column.label"/>
            </span>
          </p>

          <div class="pi-toolbar" role="group" aria-label="Filtros das oportunidades">
            <filter-select label="Status" :value="clean.status" :options="statusOptions" :all-label="openLabel"
                           @input="setFilter('status', $event)">
              <pi-definition-tooltip slot="info" class="pi-opp-info" info-key="revenue.filter.status" label="Status"/>
            </filter-select>
            <filter-select label="Regra" :value="clean.rule" :options="ruleOptionsShown" all-label="Todas"
                           @input="setFilter('rule', $event)">
              <pi-definition-tooltip slot="info" class="pi-opp-info" info-key="revenue.filter.rule" label="Regra"/>
            </filter-select>
            <div class="pi-opp-filter">
              <filter-select label="Funcionalidade" :value="clean.feature" :options="featureOptionsShown" all-label="Todas"
                             @input="setFilter('feature', $event)">
                <pi-definition-tooltip slot="info" class="pi-opp-info" info-key="revenue.filter.feature" label="Funcionalidade"/>
              </filter-select>
              <span v-if="featureError" class="pi-opp-filter__error">{{ featureError }}</span>
            </div>
            <filter-select label="Porte" :value="clean.size" :options="sizeOptions" @input="setFilter('size', $event)">
              <pi-definition-tooltip slot="info" class="pi-opp-info" info-key="revenue.filter.size" label="Porte"/>
            </filter-select>
            <div v-if="clean.company" class="pi-opp-filter">
              <span class="pi-opp-filter__label">Empresa<pi-definition-tooltip class="pi-opp-info" info-key="revenue.filter.company" label="Empresa"/></span>
              <span class="pi-opp-chip">
                <span class="pi-opp-chip__text">{{ clean.companyName || clean.company }}</span>
                <button type="button" class="pi-opp-chip__remove" :aria-label="'Remover o filtro de empresa ' + (clean.companyName || clean.company)"
                        @click="setFilter('company', '')">×</button>
              </span>
            </div>
            <a v-if="anyFilter" href="#" class="pi-link pi-opp-filter__clear" @click.prevent="clearFilters">Limpar filtros</a>
          </div>

          <pi-data-state :loading="list.loading" :error="listFailure" :envelope="list.envelope" :empty="isEmpty"
                         :empty-message="emptyText" :show-footer="false" @retry="loadList(true)">
            <ul class="pi-opps">
              <li v-for="item in items" :key="item.id || item.title" class="pi-opps__item">
                <pi-opportunity-card :item="item" :catalog="statusCatalog" @updated="applyUpdate" @reload="loadList(true)"/>
              </li>
            </ul>
            <table-pager :total="total" :page="page" :page-size="pageSize" :page-sizes="pageSizes"
                         @update:page="changePage" @update:pageSize="changePageSize"/>
          </pi-data-state>
        </section>

        <pi-page-footer :envelopes="[rules.envelope, list.envelope]" :labels="['Regras', 'Oportunidades']"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import filterSelect from "../../components/default/filterSelect.vue";
import tablePager from "../../components/default/tablePager.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piOpportunityCard from "../../components/pi/piOpportunityCard.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { errorMessage, loadFeatureOptions, loadRevenueStatuses, piGet } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportProvide } from "../../components/pi/piExport.js";
import { SIZE_BAND_FILTER_OPTIONS, toQueryString } from "../../components/pi/piQuery.js";
import { blankRequest, listPayloadOf, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import { pageOf } from "../../components/pi/piTable.js";
import {
  OPEN_FILTER_LABEL, OPPORTUNITY_COLUMNS, OPPORTUNITY_FILTER_DEFAULTS, OPPORTUNITY_FILTER_KEY, OPPORTUNITY_ORDER_TEXT, OPPORTUNITY_PAGE_SIZE,
  OPPORTUNITY_PAGE_SIZES, RULE_LEGEND, emptyOpportunitiesText, opportunitiesPayload, opportunityCountText, opportunityFilterKey,
  opportunityFilterLines, opportunityFiltersActive, opportunityParams, opportunityView, revenueError, ruleOptions, ruleRows,
  sanitizeOpportunityFilters, statusCatalog, statusFilterOptions,
} from "../../components/pi/piOpportunitiesModel.js";
import { PI_REVENUE_OPPORTUNITIES, PI_REVENUE_RULES } from "../../../../urlConfig";

const RULES_KEY = "rules";

export default {
  name: "pi-opportunities",
  components: { boxDefault, filterSelect, tablePager, piTabs, piDataState, piOpportunityCard, piPageFooter, piDefinitionTooltip, piExportMenu },
  mixins: [listFilterState(OPPORTUNITY_FILTER_KEY, OPPORTUNITY_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    return {
      exportColumns: OPPORTUNITY_COLUMNS,
      cardLegend: screenColumns(OPPORTUNITY_COLUMNS),
      ruleLegend: RULE_LEGEND,
      rules: blankRequest(),
      list: blankRequest(),
      overrides: {},
      page: 1,
      pageSize: OPPORTUNITY_PAGE_SIZE,
      pageSizes: OPPORTUNITY_PAGE_SIZES,
      openLabel: OPEN_FILTER_LABEL,
      orderText: OPPORTUNITY_ORDER_TEXT,
      statusCatalog: null,
      sizeOptions: SIZE_BAND_FILTER_OPTIONS,
      featureOptions: [],
      featureError: "",
    };
  },
  computed: {
    clean() {
      return sanitizeOpportunityFilters(this.filters);
    },
    anyFilter() {
      return opportunityFiltersActive(this.filters);
    },
    filterKey() {
      return opportunityFilterKey(this.filters);
    },
    params() {
      return opportunityParams(this.filters, this.page, this.pageSize);
    },
    apiKey() {
      return toQueryString(this.params);
    },
    exportSource() {
      return { path: PI_REVENUE_OPPORTUNITIES, query: this.params };
    },
    exportFilters() {
      return opportunityFilterLines(this.filters, { statuses: this.statusOptions, rules: this.ruleOptionsShown, features: this.featureOptionsShown });
    },
    statusOptions() {
      return statusFilterOptions(this.statusCatalog);
    },
    ruleRows() {
      return ruleRows(listPayloadOf(this.rules.envelope));
    },
    rulesEmpty() {
      return this.rules.loaded && !this.ruleRows.length;
    },
    rulesFailure() {
      return revenueError(this.rules.error);
    },
    ruleOptionsShown() {
      return ruleOptions(this.ruleRows, this.clean.rule);
    },
    featureOptionsShown() {
      const selected = this.clean.feature;
      if (!selected || this.featureOptions.some((option) => option.value === selected)) return this.featureOptions;
      return this.featureOptions.concat([{ value: selected, label: "Funcionalidade " + selected }]);
    },
    payload() {
      return opportunitiesPayload(payloadOf(this.list.envelope));
    },
    items() {
      return this.payload.items.map((item) => {
        const id = item.id === undefined || item.id === null ? "" : String(item.id);
        return opportunityView(this.overrides[id] || item, this.statusCatalog);
      });
    },
    total() {
      return this.payload.total;
    },
    isEmpty() {
      return this.list.loaded && !this.items.length;
    },
    emptyText() {
      return emptyOpportunitiesText(this.ruleRows, this.anyFilter);
    },
    listFailure() {
      return revenueError(this.list.error);
    },
    countText() {
      if (!this.list.loaded) return "";
      return opportunityCountText(this.total, this.clean.status);
    },
  },
  watch: {
    filterKey() {
      this.page = 1;
    },
    apiKey() {
      this.loadList(false);
    },
  },
  created() {
    this.loadRules(false);
    this.loadList(false);
    this.loadFeatures();
    this.loadStatuses();
  },
  methods: {
    loadRules(force) {
      if (!shouldLoad(this.rules, RULES_KEY, force)) return;
      runRequest(this.rules, () => piGet(PI_REVENUE_RULES), "carregar as regras", RULES_KEY);
    },
    loadList(force) {
      const key = this.apiKey;
      if (!shouldLoad(this.list, key, force)) return;
      runRequest(this.list, () => piGet(PI_REVENUE_OPPORTUNITIES, this.params), "carregar as oportunidades", key).then((applied) => {
        if (applied) this.overrides = {};
      });
    },
    loadFeatures() {
      loadFeatureOptions()
        .then((options) => {
          this.featureOptions = options;
          this.featureError = "";
        })
        .catch((error) => {
          this.featureError = errorMessage(error, "carregar a lista de funcionalidades");
        });
    },
    loadStatuses() {
      loadRevenueStatuses()
        .then((data) => {
          this.statusCatalog = statusCatalog(data);
        })
        .catch(() => {
          this.statusCatalog = null;
        });
    },
    setFilter(key, value) {
      const patch = {};
      patch[key] = value === null || value === undefined ? "" : String(value);
      if (key === "company" && !patch.company) patch.companyName = "";
      this.filters = Object.assign({}, this.filters, patch);
    },
    applyUpdate(updated) {
      const id = updated && updated.id !== undefined && updated.id !== null ? String(updated.id) : "";
      if (id) this.$set(this.overrides, id, updated);
    },
    changePage(page) {
      this.page = pageOf({ page: page }, 1);
    },
    changePageSize(size) {
      this.pageSize = size;
      this.page = 1;
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-opp-rules {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pi-opp-rule {
  padding: 10px 12px;
  border: 1px solid #e6e7eb;
  border-radius: 6px;
  background: #ffffff;
}

.pi-opp-rule__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.pi-opp-rule__name {
  font-size: 13px;
  color: #1f2933;
}

.pi-opp-rule__text {
  margin: 0 0 4px;
  font-size: 12px;
  color: #3a424e;
}

.pi-opp-rule__meta {
  margin: 0;
  font-size: 11px;
  color: #6b7280;
}

.pi-opp-order {
  margin: 0 0 8px;
}

.pi-opp-info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-opp-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 14px;
  margin: 0 0 10px;
  font-size: 12px;
  color: #4e5560;
}

.pi-opp-legend__lead {
  font-weight: 700;
}

.pi-opp-legend__item {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.pi-opp-filter {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pi-opp-filter__label {
  font-size: 12px;
  color: #4e5560;
}

.pi-opp-filter__error {
  font-size: 11px;
  color: #a3172b;
}

.pi-opp-filter__clear {
  padding-bottom: 8px;
  font-size: 12px;
}

.pi-opp-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 300px;
  height: 34px;
  padding: 0 6px 0 10px;
  border: 1px solid #b9cefb;
  border-radius: 17px;
  background: #eef3fe;
  font-size: 12px;
  color: #1a45b8;
}

.pi-opp-chip__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pi-opp-chip__remove {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #1a45b8;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
}

.pi-opp-chip__remove:hover {
  background: #dbe6fd;
}

.pi-opps {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 0 8px;
  padding: 0;
  list-style: none;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piOpportunities.vue